import { Controller } from "@hotwired/stimulus"
import { hasUnsavedChangesOnPage, clearUnsavedChangesOnPage } from "../../pwa/unsaved_changes"

// Connects to data-controller="mode-toggle"
export default class extends Controller {
  static targets = [
    "button", "title", "description", "groupInfo", "caption",
    "individualConfirm", "groupConfirm", "individualForm"
  ]

  static values = {
    default: String,
    photosUrl: String
  }

  connect() {
    this.switchTo(this.defaultValue || "individual")
    this.#prefetchPhotosPage()
  }

  // Warms the service worker cache for the photos page while online so users
  // who later go offline can still navigate there. We hit the no-param URL —
  // the SW falls back to this entry via ignoreSearch for any query variant.
  #prefetchPhotosPage() {
    if (!navigator.onLine) return
    if (!this.hasPhotosUrlValue || !this.photosUrlValue) return
    fetch(this.photosUrlValue, { credentials: "same-origin" }).catch(() => {})
  }

  select(event) {
    const mode = event.currentTarget.dataset.mode
    const currentMode = this.buttonTargets.find(b => b.classList.contains("active"))?.dataset.mode
    if (mode === currentMode) return

    const dirty = hasUnsavedChangesOnPage(this.application)
    if (dirty && !window.confirm("未保存の変更があります。モードを切り替えますか？")) {
      return
    }

    if (dirty) {
      // User already confirmed above; clear dirty flags so the global
      // turbo:before-visit guard does not prompt again, then re-render from
      // the server to discard local edits.
      clearUnsavedChangesOnPage(this.application)
      Turbo.visit(this.#urlForMode(mode))
      return
    }

    this.switchTo(mode)
    this.#persistMode(mode)
  }

  #urlForMode(mode) {
    const url = new URL(window.location.href)
    if (mode === "group") {
      url.searchParams.set("mode", "group")
    } else {
      url.searchParams.delete("mode")
    }
    return url.toString()
  }

  openPhotos(event) {
    event?.preventDefault()
    if (!this.hasPhotosUrlValue || !this.photosUrlValue) return

    const mode = document.querySelector('input[name="mode"]')?.value || this.defaultValue || "individual"
    const url = new URL(this.photosUrlValue, window.location.origin)
    url.searchParams.set("mode", mode)

    const workerIds = []
    if (mode === "group") {
      document.querySelectorAll(".member-checkbox:checked").forEach(cb => {
        url.searchParams.append("worker_ids[]", cb.value)
        workerIds.push(cb.value)
      })
    }

    // Stash context so the photos page can recover it even when SW returns a
    // cached HTML rendered with different mode/worker_ids (offline scenario).
    try {
      sessionStorage.setItem("photo_nav_mode", mode)
      sessionStorage.setItem("photo_nav_worker_ids", JSON.stringify(workerIds))
    } catch (e) { /* sessionStorage may be unavailable */ }

    window.location.href = url.toString()
  }

  #persistMode(mode) {
    const url = new URL(window.location.href)
    if (mode === "group") {
      url.searchParams.set("mode", "group")
    } else {
      url.searchParams.delete("mode")
    }
    window.history.replaceState({}, "", url.toString())
  }

  switchTo(mode) {
    this.buttonTargets.forEach(btn => {
      btn.classList.toggle("active", btn.dataset.mode === mode)
    })

    document
      .querySelectorAll('input[name="mode"]')
      .forEach(input => input.value = mode)

    if (mode === "individual") {
      this.titleTarget.textContent = "勤怠打刻（個人）"
      this.descriptionTarget.textContent = "スマホから出勤・退勤を登録します（ログイン中の職員）"
      this.captionTarget.textContent = "個人モード：ログイン中の職員だけを打刻します。"

      this.groupInfoTarget.style.display = "none"
      if (this.hasIndividualFormTarget) this.individualFormTarget.style.display = ""
      if (this.hasIndividualConfirmTarget) this.individualConfirmTarget.style.display = ""
      if (this.hasGroupConfirmTarget) this.groupConfirmTarget.style.display = "none"
    } else {
      this.titleTarget.textContent = "勤怠打刻（班一括）"
      this.descriptionTarget.textContent = "作業班の中から選択した班員を一括で打刻します。"
      const teamName = document.querySelector("#team-selector-form select")?.selectedOptions[0]?.text || "班"
      this.captionTarget.textContent = `${teamName} の中からチェックした班員を一括で打刻します。`

      this.groupInfoTarget.style.display = "block"
      if (this.hasIndividualFormTarget) this.individualFormTarget.style.display = "none"
      if (this.hasIndividualConfirmTarget) this.individualConfirmTarget.style.display = "none"
      if (this.hasGroupConfirmTarget) this.groupConfirmTarget.style.display = ""
    }
  }
}
